import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
	type QueryParsingFailure,
	type QueryStreamRequest,
	Type,
	type ViewSnapshot,
	type ViewSnapshotError,
	type ViewSnapshotRequest,
	isQueryStreamRequest,
	isViewSnapshotRequest,
} from "~/models/dto";
import { identity } from "~/tools/identity";

import { useWebSocketStream } from "../../../components/_websocket";
import { queryTopologySnapshot } from "../../../queries";
import { useQuery } from "../../../queries/react-query";
import { selectDefaultQueryVersion } from "../config/ConfigSelectors";
import { selectTimestampDate } from "../view/timeline/TimelineSelectors";
import { forValidatedQuery, metaDataComponentsOnly, metaDataFromView } from "../view/viewpoint/ViewPointHelpers";
import { useCurrentView } from "../view/viewpoint/ViewPointHooks";

import { TopologySnapshot } from "./TopologySnapshot";

interface UseTopologySnapshotOptions<TResult> {
	/**
	 * When `true` fetch only components and ignore grouping, relations, and other metadata.
	 *
	 * @default false
	 */
	isComponentsOnly?: boolean;

	select?: (data: TopologySnapshot) => TResult;
}

interface UseTopologySnapshotResult<TResult> {
	isLive: boolean;
	isLoading: boolean;
	error: null | Error | QueryParsingFailure | ViewSnapshotError;
	data: undefined | TResult;
}

export function useTopologySnapshot(): UseTopologySnapshotResult<TopologySnapshot>;
export function useTopologySnapshot<TResult>(
	options: UseTopologySnapshotOptions<TResult>,
): UseTopologySnapshotResult<TResult>;

export function useTopologySnapshot<TResult>({
	isComponentsOnly = false,
	select = identity as (snapshot: TopologySnapshot) => TResult,
}: UseTopologySnapshotOptions<TResult> = {}): UseTopologySnapshotResult<TResult> {
	const time = useSelector(selectTimestampDate);
	const defaultQueryVersion = useSelector(selectDefaultQueryVersion);

	const request = useCurrentView()
		.transform((view) => {
			const queryVersion = view.queryVersion || defaultQueryVersion;
			const metadata = isComponentsOnly ? metaDataComponentsOnly(time) : metaDataFromView(view, time);

			if (time == null) {
				return forValidatedQuery<QueryStreamRequest>(view.query, (query) => ({
					_type: Type.QueryStreamRequest,
					metadata,
					query,
					queryVersion,
				}));
			}

			return forValidatedQuery<ViewSnapshotRequest>(view.query, (query) => ({
				_type: Type.ViewSnapshotRequest,
				viewId: view.id,
				metadata,
				query,
				queryVersion,
			}));
		})
		.getOrElse(undefined);

	const ws = useWebSocketStream({
		request: isQueryStreamRequest(request) ? request : undefined,
		select,
	});

	const http = useQuery({
		select: useCallback((snapshot: ViewSnapshot) => select(TopologySnapshot.fromViewSnapshot(snapshot)), [select]),

		...queryTopologySnapshot(isViewSnapshotRequest(request) ? request : undefined),
	});

	if (time == null) {
		return {
			isLive: true,
			isLoading: ws.isLoading,
			error: ws.error,
			data: ws.data,
		};
	}

	return {
		isLive: false,
		isLoading: http.isLoading || http.isIdle,
		error: http.error,
		data: http.data,
	};
}
